/**
 * Server-driven dex config: read the admin-set curated-roster + release
 * overrides from the Worker and merge them over the client's bundled baseline.
 *
 * GET /api/dex-config returns the overrides an admin has set in the portal.
 * Applying them lets a newly-released Mega (e.g. Mega Starmie) appear, or a
 * species flip released↔unreleased, app-wide with no rebuild. The endpoint is
 * public and always returns a complete body (empty overrides when unset), so a
 * successful response is always safe to apply. Until it resolves, consumers run
 * on the bundled baseline.
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { setRuntimeCuratedRosterOverrides, ROSTER_OVERRIDE_VARIANT_KEYS } from "@/lib/pogoAvailability";
import { setReleaseSchedule } from "@/lib/variantAvailability";
import type { ReleaseScheduleWire, ScheduledDexId } from "@/lib/releaseSchedule";
import { setRuntimeVariantLabels } from "@/components/dexVariantVisuals";
import { setRuntimeDexOrder } from "@/lib/dexVisibilityStore";
import { setRuntimeMegaSpriteOverrides, type MegaSpriteOverride } from "@/lib/megaGmaxSprites";

type RosterVariantKey = "mega" | "gmax" | "dynamax" | "shadow" | "purified";

export interface DexConfig {
  roster: Record<RosterVariantKey, { add: ScheduledDexId[]; remove: number[] }>;
  /** Per-variant, date-aware release overrides (see releaseSchedule.ts). */
  releaseSchedule: ReleaseScheduleWire;
  variants: { labels: Record<string, string>; order: Record<string, number> };
  spriteOverrides?: Array<{ variant: RosterVariantKey; dexId: number; slug: string; formId: number | null }>;
}

const DEX_CONFIG_URL = "/api/dex-config";

export function useDexConfig(): { data: DexConfig | undefined; isResolved: boolean } {
  const query = useQuery<DexConfig>({
    queryKey: [DEX_CONFIG_URL],
    staleTime: 5 * 60 * 1000,
    gcTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await apiRequest("GET", DEX_CONFIG_URL);
      return res.json();
    },
  });

  const data = query.data;
  useEffect(() => {
    if (!data) return;
    const roster = data.roster;
    if (roster) {
      ROSTER_OVERRIDE_VARIANT_KEYS.forEach((v) => {
        const o = roster[v];
        // add entries carry an optional releaseAt; setRuntimeCuratedRosterOverrides
        // date-gates them at read time.
        if (o) setRuntimeCuratedRosterOverrides(v, o.add ?? [], o.remove ?? []);
      });
    }
    // Per-variant, date-aware release schedule (base cascades to lucky/perfect/
    // xxs/xxl; shiny cascades to shundo/shinyLucky). Read-time auto-flip.
    setReleaseSchedule(data.releaseSchedule ?? {});
    if (data.variants) {
      setRuntimeVariantLabels(data.variants.labels ?? {});
      setRuntimeDexOrder(data.variants.order ?? {});
    }
    // Sprite provenance for admin-added mega/gmax members (dynamax uses base art).
    const sprites: MegaSpriteOverride[] = (data.spriteOverrides ?? [])
      .filter((s) => s.variant === "mega" || s.variant === "gmax")
      .map((s) => ({ variant: s.variant as "mega" | "gmax", baseId: s.dexId, slug: s.slug, formId: s.formId }));
    setRuntimeMegaSpriteOverrides(sprites);
  }, [data]);

  return { data, isResolved: query.isSuccess };
}
